/**
 * RWBR — THE ONE SAVED LEAGUE. D-EZ-99 rework, 4 Oct 2026.
 *
 * Howard, 17 Aug 2026:
 *
 *   "They need to be able to register with email and password and save their
 *    leagues and reload them when they come back. Only one league at a time,
 *    always rewriting the same slot if they so choose to."
 *
 * and, 4 Oct 2026, approving the saving rework: one Save, one Load, every
 * season kept, no ceiling, and a copy on the device.
 *
 * ===========================================================================
 * WHAT A SAVED LEAGUE IS NOW
 * ===========================================================================
 *
 *   league_slots         ONE row per account — the HEAD. Which league
 *                        (`league_id`), its name, the season on the workbench,
 *                        and the list of settled seasons behind it. Small.
 *   league_slot_seasons  ONE row per settled season of that league, the race
 *                        tapes stripped (`seasonArchive.ts`). ~2 MB stored each,
 *                        written ONCE: a season does not change after its winter.
 *   league_slot_races    ONE row per finished race of the season in progress
 *                        (`seasonSlot.ts`), written once each.
 *
 * So a save sends the head plus only what the server does not already hold —
 * normally one race — and a league can run for as many seasons as anyone plays:
 * the old ~14-season ceiling (every season inside one 30 MB row) is gone, and so
 * is the mid-season head that re-sent the whole shelf on every press.
 *
 * THE HEAD IS WRITTEN LAST and it is the only thing a load believes. Seasons and
 * races go up first; if anything fails before the head, the previous save is
 * still whole and the new rows are inert. A load refuses unless every season and
 * race the head names is stored and belongs to the head's league.
 *
 * LEAGUE ISOLATION. Every season row carries the league id twice — in its key
 * and inside its blob — and a load refuses a season from another league rather
 * than folding it in. Leagues never merge.
 *
 * The same pure pack/unpack (`packLeague`, `unpackLeague`) is used by the cloud
 * save, the device backup (`localBackup.ts`) and the league file
 * (`leagueFile.ts`), so the three can never disagree about what a league is.
 *
 * OLD SAVES (format 1–3) ARE REFUSED with one friendly sentence. Howard ruled
 * that existing careers need not be kept; the migration empties the old rows.
 */

import type { CareerCarry } from '../domain/careerCarry.ts';
import { normaliseSettings, type LeagueSettings } from './leagueSettings.ts';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase as defaultClient } from './supabase.ts';
import type { CareerSeason } from './careerRunner.ts';
import { archiveSeason, restoreArchived, shelfOrder } from './seasonArchive.ts';
import type { DriverCard } from '../domain/types.ts';
import type { Team } from '../domain/field.ts';
import { SEASON_RACES } from '../domain/schedule.ts';
import {
  openSeasonSlot,
  readSeasonRaceChunk,
  readSeasonSlotHead,
  rebuildSeasonState,
  splitSeasonSave,
  type LiveSeason,
  type SeasonRaceChunk,
  type SeasonRef,
  type SeasonSlotHead,
} from './seasonSlot.ts';

export type { SeasonRef } from './seasonSlot.ts';

/** The head table. */
export const SLOT_TABLE = 'league_slots';
/** One row per settled season. `supabase/migrations/20261005000000_league_saves_v4.sql`. */
export const SLOT_SEASON_TABLE = 'league_slot_seasons';
/** One row per finished race of the season in progress. */
export const SLOT_RACE_TABLE = 'league_slot_races';

export type SlotKind = 'career' | 'season';

/**
 * 4 — D-EZ-99: league id on everything, seasons in their own rows, the head no
 * longer carries the shelf. Nothing older is read (see the header).
 */
export const SLOT_FORMAT_VERSION = 4;
export const SLOT_OLDEST_READABLE = 4;

/** Races fetched per request on a load. One race is ~0.3 MB stored. */
export const CHUNK_PAGE_RACES = 6;
/** Bytes of race rows sent per statement. Binds only on a first save of a long offline run. */
export const MAX_CHUNK_UPLOAD_BYTES = 4 * 1024 * 1024;
export const SLOT_ENCODING = 'gzip+base64';

/**
 * A TRIPWIRE PER ROW, not per league. A settled season stores at ~2 MB and a
 * race at ~0.3 MB, so 16 MB is room for a far bigger field before anything
 * binds — and a refusal that names the size beats a proxy's bare 413.
 */
export const MAX_PAYLOAD_BYTES = 16 * 1024 * 1024;

/** How long one statement may take before the player is told plainly. */
export const STATEMENT_TIMEOUT_MS = 20_000;
/** A season row is the biggest single thing sent; it gets longer. */
export const BIG_STATEMENT_TIMEOUT_MS = 60_000;

// ---------------------------------------------------------------------------
// What is in a save
// ---------------------------------------------------------------------------

/** The post-winter state needed to start season N+1 — `nextSeasonOptions()`'s inputs. */
export interface SlotCarry {
  roster: DriverCard[];
  teams: Team[];
  seasonsRun: Record<string, number>;
  /**
   * D-EZ-91/94/97 — everything else the winter hands on: sponsor contracts,
   * shop banks, drivers' money, live feuds, injuries, suspensions and career
   * priors (`OffseasonResult.carry`). Without it a reloaded league would start
   * its next season on fresh sponsors and empty banks. (Ages ride on the cards.)
   */
  career?: CareerCarry;
}

/** The head when no season is in progress: the latest settled season and its winter. */
export interface CareerHead {
  v: number;
  kind: 'career';
  leagueId: string;
  leagueName: string;
  seasonNumber: number;
  seed: number;
  carry: SlotCarry;
  /** Every settled season of the league, oldest first. Includes `seasonNumber`. */
  seasons: SeasonRef[];
  /** D-EZ-103 — the league settings the NEXT season starts with. */
  settings?: LeagueSettings;
}

/** D-EZ-103 — a season head carries the league settings beside it too. */
export type LeagueHead = CareerHead | (SeasonSlotHead & { settings?: LeagueSettings });

/** One settled season as stored: the archived season, labelled with its league. */
export interface PackedSeason {
  v: number;
  leagueId: string;
  seasonNumber: number;
  seed: number;
  season: CareerSeason;
}

/**
 * A WHOLE LEAGUE, PACKED. `seasons` are thunks so a cloud save that already
 * holds nine of ten seasons archives only the tenth.
 */
export interface LeaguePack {
  head: LeagueHead;
  seasons: { ref: SeasonRef; pack: () => PackedSeason }[];
  races: SeasonRaceChunk[];
}

/** What the shell hands a save. */
export interface LeagueToSave {
  leagueId: string;
  leagueName: string;
  /** The season being played race by race, if any. */
  season?: LiveSeason | null;
  /** The latest settled season, if any. */
  career?: CareerSeason | null;
  /** Earlier settled seasons of the same league, oldest first. */
  history?: readonly CareerSeason[];
  /**
   * D-EZ-103 — the league settings the next season will start with. Saved on the
   * head of either kind; a season in progress also keeps its own in its state.
   */
  settings?: LeagueSettings;
}

export interface SlotSummary {
  kind: SlotKind;
  leagueId: string | null;
  leagueName: string;
  seasonNumber: number;
  seed: number;
  /** Races in the books for a season in progress; null for a finished season. */
  racesRun: number | null;
  seasonKey: string | null;
  savedAt: string;
  formatVersion: number;
}

/** A finished season, opened. */
export interface LoadedCareer {
  kind: 'career';
  leagueId: string;
  leagueName: string;
  seasonNumber: number;
  seed: number;
  savedAt: string;
  carry: SlotCarry;
  /** Every settled season, oldest first, derived fields put back. */
  archive: CareerSeason[];
  /** D-EZ-103 — the league settings, when the save carried them. */
  settings?: LeagueSettings;
}

/** A season in progress, opened — ready for its next green flag. */
export interface LoadedSeasonInProgress {
  kind: 'season';
  leagueId: string;
  leagueName: string;
  seasonNumber: number;
  seed: number;
  savedAt: string;
  racesRun: number;
  season: LiveSeason;
  archive: CareerSeason[];
  /** D-EZ-103 — the league settings for the next season, when the save carried them. */
  settings?: LeagueSettings;
}

export type LoadedLeague = LoadedCareer | LoadedSeasonInProgress;

// ---------------------------------------------------------------------------
// Failure, named — in plain words
// ---------------------------------------------------------------------------

export type SlotFailureCode =
  | 'not-signed-in'
  | 'nothing-to-save'
  | 'no-saved-league'
  | 'too-large'
  | 'format-too-new'
  | 'format-too-old'
  | 'corrupt'
  | 'unsupported-browser'
  | 'denied'
  | 'table-missing'
  | 'offline'
  | 'server-error'
  | 'unknown';

export interface SlotFailure {
  ok: false;
  code: SlotFailureCode;
  message: string;
  detail?: string;
}

export interface SlotSaved {
  ok: true;
  summary: SlotSummary;
  /** Compressed characters sent by THIS save. Owner diagnostics only. */
  bytes: number;
  /** Settled seasons sent by this save, and held in total. */
  seasonsWritten: number;
  seasonsHeld: number;
  /** Races sent by this save, and held in total; null when no season is in progress. */
  racesWritten: number | null;
  racesHeld: number | null;
}

export interface SlotLoaded {
  ok: true;
  league: LoadedLeague;
}

export interface SlotPeeked {
  ok: true;
  summary: SlotSummary | null;
}

export interface SlotDeleted {
  ok: true;
  removed: SlotSummary | null;
}

/** The player-facing sentence for a save from before D-EZ-99. */
export const OLD_SAVE_MESSAGE =
  "This save is from an older version of the game and can't be opened — start a new league.";

/** The player-facing sentence when the save server cannot be reached. */
export const UNREACHABLE_MESSAGE =
  "Couldn't reach the save server — your league is still backed up on this device. Try again.";

const failure = (code: SlotFailureCode, message: string, detail?: string): SlotFailure => ({
  ok: false,
  code,
  message,
  ...(detail ? { detail } : {}),
});

// ---------------------------------------------------------------------------
// League ids
// ---------------------------------------------------------------------------

/** A fresh league id. A UUID where the platform has one. */
export function newLeagueId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `lg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// Pure: packing and unpacking a league
// ---------------------------------------------------------------------------

/** The settled seasons a save carries, oldest first, one per season number. */
export function shelfOf(career: CareerSeason | null | undefined, history: readonly CareerSeason[] = []): CareerSeason[] {
  if (career) return shelfOrder(career, history);
  const byNumber = new Map<number, CareerSeason>();
  for (const s of history) byNumber.set(s.seasonNumber, s);
  return [...byNumber.values()].sort((a, b) => a.seasonNumber - b.seasonNumber);
}

/**
 * Turn the league on the workbench into what is stored. Pure.
 *
 * A season in progress wins over a settled one: it is the thing being played.
 * With neither, there is nothing to save.
 */
export function packLeague(input: LeagueToSave): LeaguePack | SlotFailure {
  const leagueId = input.leagueId?.trim();
  if (!leagueId) return failure('nothing-to-save', 'There is no league to save yet.');
  const name = input.leagueName.trim() || 'RASCAL';
  const shelf = shelfOf(input.career, input.history ?? []);
  const refs: SeasonRef[] = shelf.map((s) => ({ seasonNumber: s.seasonNumber, seed: s.seed }));
  const seasons = shelf.map((s) => ({
    ref: { seasonNumber: s.seasonNumber, seed: s.seed },
    pack: (): PackedSeason => ({
      v: SLOT_FORMAT_VERSION,
      leagueId,
      seasonNumber: s.seasonNumber,
      seed: s.seed,
      season: archiveSeason(s),
    }),
  }));

  if (input.season) {
    const live: LiveSeason = { ...input.season, leagueId };
    const { head, chunks } = splitSeasonSave(live, name, SLOT_FORMAT_VERSION, refs, leagueId);
    return { head: input.settings ? { ...head, settings: normaliseSettings(input.settings) } : head, seasons, races: chunks };
  }

  const career = input.career ?? shelf[shelf.length - 1];
  if (!career) {
    return failure(
      'nothing-to-save',
      'Nothing to save yet — start a league on Home.',
    );
  }
  const head: CareerHead = {
    v: SLOT_FORMAT_VERSION,
    kind: 'career',
    leagueId,
    leagueName: name,
    seasonNumber: career.seasonNumber,
    seed: career.seed,
    carry: {
      roster: career.offseason.roster,
      teams: career.offseason.teams,
      seasonsRun: career.offseason.seasonsRun,
      ...(career.offseason.carry ? { career: career.offseason.carry } : {}),
    },
    seasons: refs,
    ...(input.settings ? { settings: normaliseSettings(input.settings) } : {}),
  };
  return { head, seasons, races: [] };
}

/** Pure. The version a stored thing claims, read the same way everywhere. */
function versionProblem(v: unknown): SlotFailure | null {
  if (typeof v !== 'number' || v < SLOT_OLDEST_READABLE) return failure('format-too-old', OLD_SAVE_MESSAGE);
  if (v > SLOT_FORMAT_VERSION) {
    return failure(
      'format-too-new',
      'This save was made by a newer version of the game. Reload the page to pick it up — and do not save over it from here.',
    );
  }
  return null;
}

/** Read a head blob, refusing anything this build cannot honestly open. */
export function readLeagueHead(raw: unknown): LeagueHead | SlotFailure {
  if (raw === null || typeof raw !== 'object') return failure('corrupt', 'The saved league could not be read.');
  const v = versionProblem((raw as { v?: unknown }).v);
  if (v) return v;
  const kind = (raw as { kind?: unknown }).kind;
  if (kind === 'season') {
    const head = readSeasonSlotHead(raw);
    if ('ok' in head) return failure('corrupt', `The saved season is incomplete — ${head.problem}.`);
    const settings = (raw as { settings?: unknown }).settings;
    return settings && typeof settings === 'object' ? { ...head, settings: normaliseSettings(settings) } : head;
  }
  const p = raw as Partial<CareerHead>;
  const missing = [
    typeof p.leagueId !== 'string' || !p.leagueId ? 'the league it belongs to' : null,
    !p.carry || typeof p.carry !== 'object' ? 'the winter' : null,
    !Array.isArray(p.carry?.roster) ? "the winter's roster" : null,
    !Array.isArray(p.carry?.teams) ? "the winter's garage" : null,
    typeof p.seasonNumber !== 'number' ? 'the season number' : null,
    !Array.isArray(p.seasons) ? 'the list of seasons' : null,
  ].filter((x): x is string => x !== null);
  if (kind !== 'career' || missing.length) {
    return failure('corrupt', `The saved league is incomplete — it is missing ${missing.join(', ') || 'its kind'}.`);
  }
  return {
    v: p.v as number,
    kind: 'career',
    leagueId: p.leagueId as string,
    leagueName: typeof p.leagueName === 'string' && p.leagueName.trim() ? p.leagueName : 'RASCAL',
    seasonNumber: p.seasonNumber as number,
    seed: typeof p.seed === 'number' ? p.seed : 0,
    carry: {
      roster: p.carry!.roster as DriverCard[],
      teams: p.carry!.teams as Team[],
      seasonsRun: (p.carry!.seasonsRun ?? {}) as Record<string, number>,
      ...(p.carry!.career && typeof p.carry!.career === 'object' ? { career: p.carry!.career as CareerCarry } : {}),
    },
    seasons: (p.seasons as SeasonRef[]).map((s) => ({ seasonNumber: Number(s?.seasonNumber), seed: Number(s?.seed) })),
    ...(p.settings && typeof p.settings === 'object' ? { settings: normaliseSettings(p.settings) } : {}),
  };
}

/** Read one stored season, checking it is the one asked for and from this league. */
export function readPackedSeason(raw: unknown, want: SeasonRef, leagueId: string): PackedSeason | SlotFailure {
  if (raw === null || typeof raw !== 'object') {
    return failure('corrupt', `Season ${want.seasonNumber} of the saved league could not be read.`);
  }
  const p = raw as Partial<PackedSeason>;
  const v = versionProblem(p.v);
  if (v) return v;
  if (p.leagueId !== leagueId) {
    // ⚠ THE ISOLATION RULE. Folding this season in would merge two leagues.
    return failure(
      'corrupt',
      `Season ${want.seasonNumber} on file belongs to a different league, so it was not folded into this one.`,
    );
  }
  if (p.seasonNumber !== want.seasonNumber || p.seed !== want.seed || !p.season || typeof p.season !== 'object') {
    return failure('corrupt', `Season ${want.seasonNumber} on file is not the season this league expects.`);
  }
  return p as PackedSeason;
}

/**
 * Pure. A head, its seasons and its races, back into a league — or a refusal.
 * Every season the head names must be present, in order, from this league.
 */
export function unpackLeague(
  head: LeagueHead,
  seasons: readonly PackedSeason[],
  races: readonly SeasonRaceChunk[],
  savedAt: string,
): LoadedLeague | SlotFailure {
  if (seasons.length !== head.seasons.length) {
    return failure(
      'corrupt',
      `The saved league names ${head.seasons.length} season${head.seasons.length === 1 ? '' : 's'} but ${seasons.length} ${seasons.length === 1 ? 'is' : 'are'} stored.`,
    );
  }
  for (const [i, ref] of head.seasons.entries()) {
    const checked = readPackedSeason(seasons[i], ref, head.leagueId);
    if ('ok' in checked) return checked;
  }
  const archive = seasons.map((p) => restoreArchived(p.season));

  if (head.kind === 'season') {
    const rebuilt = rebuildSeasonState(head, races);
    if ('problem' in rebuilt) {
      return failure('corrupt', `The saved season could not be put back together — ${rebuilt.problem}.`);
    }
    return {
      kind: 'season',
      leagueId: head.leagueId,
      leagueName: head.leagueName,
      seasonNumber: head.seasonNumber,
      seed: head.seed,
      savedAt,
      racesRun: head.racesRun,
      season: openSeasonSlot(head, rebuilt.state),
      archive,
      ...(head.settings ? { settings: head.settings } : {}),
    };
  }
  return {
    kind: 'career',
    leagueId: head.leagueId,
    leagueName: head.leagueName,
    seasonNumber: head.seasonNumber,
    seed: head.seed,
    savedAt,
    carry: head.carry,
    archive,
    ...(head.settings ? { settings: head.settings } : {}),
  };
}

/** Read one stored race of the season in progress. */
export function readSeasonRaceRow(raw: unknown, expected: number): SeasonRaceChunk | SlotFailure {
  const chunk = readSeasonRaceChunk(raw, expected);
  if ('ok' in chunk) return failure('corrupt', `The saved season could not be put back together — ${chunk.problem}.`);
  return chunk;
}

// ---------------------------------------------------------------------------
// The codec — lossless: JSON → gzip → base64
// ---------------------------------------------------------------------------

export function compressionAvailable(): boolean {
  return typeof CompressionStream === 'function' && typeof DecompressionStream === 'function';
}

const NO_COMPRESSION: SlotFailure = failure(
  'unsupported-browser',
  'This browser is too old to save a league. Everything else in the game works; a browser from 2023 or later can save.',
);

/**
 * Push bytes through a transform stream. The `.catch()` on the write side is
 * load-bearing: on bad gzip BOTH ends reject, and an unawaited rejection would
 * take the process down instead of showing a message.
 */
async function through(bytes: Uint8Array, transform: GenericTransformStream): Promise<Uint8Array> {
  const writer = (transform.writable as WritableStream<Uint8Array>).getWriter();
  const pumped = (async () => {
    await writer.write(bytes);
    await writer.close();
  })().catch(() => undefined);
  const chunks: Uint8Array[] = [];
  let total = 0;
  const reader = (transform.readable as ReadableStream<Uint8Array>).getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      total += value.length;
    }
  } finally {
    await pumped;
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

/** Base64 in chunks — the one-liner overflows the call stack on a real season. */
const B64_CHUNK = 0x8000;
function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += B64_CHUNK) s += String.fromCharCode(...bytes.subarray(i, i + B64_CHUNK));
  return btoa(s);
}
function fromBase64(text: string): Uint8Array {
  const bin = atob(text);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** gzip bytes of a JSON value. Shared with the league file. */
export async function gzipJson(value: unknown): Promise<Uint8Array> {
  return through(new TextEncoder().encode(JSON.stringify(value)), new CompressionStream('gzip'));
}

/** The JSON value inside gzip bytes. */
export async function gunzipJson(bytes: Uint8Array): Promise<{ value: unknown } | SlotFailure> {
  if (!compressionAvailable()) return NO_COMPRESSION;
  let text: string;
  try {
    text = new TextDecoder().decode(await through(bytes, new DecompressionStream('gzip')));
  } catch (e) {
    return failure('corrupt', 'The saved league is damaged and could not be unpacked.', String((e as Error)?.message ?? e));
  }
  try {
    return { value: JSON.parse(text) as unknown };
  } catch (e) {
    return failure('corrupt', 'The saved league unpacked but could not be read.', String((e as Error)?.message ?? e));
  }
}

export async function encodeSlotBlob(payload: unknown): Promise<string> {
  return toBase64(await gzipJson(payload));
}

export async function decodeBlobJson(blob: unknown): Promise<{ value: unknown } | SlotFailure> {
  if (typeof blob !== 'string' || !blob) return failure('corrupt', 'The saved league is empty.');
  if (!compressionAvailable()) return NO_COMPRESSION;
  let bytes: Uint8Array;
  try {
    bytes = fromBase64(blob);
  } catch (e) {
    return failure('corrupt', 'The saved league is damaged and could not be unpacked.', String((e as Error)?.message ?? e));
  }
  return gunzipJson(bytes);
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/** The head row. Label columns are derived from the head in the same expression. */
export async function buildHeadRow(userId: string, head: LeagueHead): Promise<Record<string, unknown>> {
  return {
    user_id: userId,
    league_id: head.leagueId,
    kind: head.kind,
    league_name: head.leagueName,
    season_number: head.seasonNumber,
    seed: head.seed,
    race_number: head.kind === 'season' ? head.racesRun : null,
    season_key: head.kind === 'season' ? head.seasonKey : null,
    format_version: head.v,
    payload_encoding: SLOT_ENCODING,
    payload: await encodeSlotBlob(head),
  };
}

export async function buildSeasonRow(userId: string, packed: PackedSeason): Promise<Record<string, unknown>> {
  return {
    user_id: userId,
    league_id: packed.leagueId,
    season_number: packed.seasonNumber,
    season_seed: packed.seed,
    payload_encoding: SLOT_ENCODING,
    payload: await encodeSlotBlob(packed),
  };
}

export async function buildSeasonRaceRow(
  userId: string,
  seasonKey: string,
  chunk: SeasonRaceChunk,
): Promise<Record<string, unknown>> {
  return {
    user_id: userId,
    season_key: seasonKey,
    race_number: chunk.raceNumber,
    payload_encoding: SLOT_ENCODING,
    payload: await encodeSlotBlob(chunk),
  };
}

/** Pure. A database row, read as a summary. */
export function describeSlotRow(row: Record<string, unknown> | null | undefined): SlotSummary | null {
  if (!row) return null;
  const kind: SlotKind = row.kind === 'season' ? 'season' : 'career';
  const raceNumber = row.race_number;
  return {
    kind,
    leagueId: typeof row.league_id === 'string' ? row.league_id : null,
    leagueName: String(row.league_name ?? 'RASCAL'),
    seasonNumber: Number(row.season_number ?? 0),
    seed: Number(row.seed ?? 0),
    racesRun: kind === 'season' && raceNumber !== null && raceNumber !== undefined ? Number(raceNumber) : null,
    seasonKey: kind === 'season' && typeof row.season_key === 'string' ? row.season_key : null,
    savedAt: String(row.saved_at ?? ''),
    formatVersion: Number(row.format_version ?? 0),
  };
}

/** What a save is ABOUT to be, for the overwrite confirmation. */
export interface IncomingSave {
  kind: SlotKind;
  leagueId?: string | null;
  leagueName: string;
  seasonNumber: number;
  racesRun: number | null;
}

/** Pure. What a save holds, in a phrase. */
export function describeSlotContents(what: {
  kind: SlotKind;
  leagueName: string;
  seasonNumber: number;
  racesRun: number | null;
}): string {
  if (what.kind === 'season') {
    const run = what.racesRun ?? 0;
    return `${what.leagueName} — season ${what.seasonNumber}, ${run} of ${SEASON_RACES} races run`;
  }
  return `${what.leagueName} — season ${what.seasonNumber}, finished`;
}

/**
 * Pure. Whether saving over `existing` needs a confirmation at all. Saving the
 * SAME league forward is the ordinary press of Save and asks nothing; replacing
 * a different league, an older save from an old version, or going backwards does.
 */
export function overwriteNeedsConfirm(existing: SlotSummary, incoming: IncomingSave): boolean {
  if (existing.formatVersion < SLOT_OLDEST_READABLE) return false; // unopenable anyway
  if (!incoming.leagueId || existing.leagueId !== incoming.leagueId) return true;
  if (incoming.seasonNumber < existing.seasonNumber) return true;
  if (incoming.seasonNumber === existing.seasonNumber) {
    if (existing.kind === 'career' && incoming.kind === 'season') return false;
    if (existing.kind === 'season' && incoming.kind === 'season') {
      return (incoming.racesRun ?? 0) < (existing.racesRun ?? 0);
    }
    if (existing.kind === 'season' && incoming.kind === 'career') return false;
  }
  return false;
}

/** Pure. One sentence naming exactly what an overwrite would replace. */
export function describeOverwrite(existing: SlotSummary, incoming: IncomingSave): string {
  const when = formatSavedAt(existing.savedAt);
  const head = `This replaces your saved league: ${describeSlotContents(existing)}, saved ${when}.`;
  let tail = '';
  const sameLeague = Boolean(incoming.leagueId) && existing.leagueId === incoming.leagueId;
  if (!sameLeague) {
    tail = ` The league you are saving is a different one (${describeSlotContents(incoming)}), and the saved one would be gone.`;
  } else if (incoming.seasonNumber < existing.seasonNumber) {
    tail = ` You are about to save season ${incoming.seasonNumber}, which is EARLIER than the saved one — seasons ${incoming.seasonNumber + 1} to ${existing.seasonNumber} would be lost.`;
  } else if (existing.kind === 'season' && incoming.kind === 'season') {
    const was = existing.racesRun ?? 0;
    const now = incoming.racesRun ?? 0;
    if (now < was) {
      tail = ` You are about to save race ${now}, which is EARLIER than the saved race ${was} — ${was - now} race${was - now === 1 ? '' : 's'} would be lost.`;
    }
  }
  return `${head}${tail} Your account keeps one league, and there is no undo.`;
}

/** Pure. A timestamp a person can read. */
export function formatSavedAt(iso: string): string {
  if (!iso) return 'at an unknown time';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'at an unknown time';
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// Pure: what a server error means, in plain words
// ---------------------------------------------------------------------------

interface PgErrorLike {
  code?: unknown;
  message?: unknown;
  details?: unknown;
  status?: unknown;
  name?: unknown;
}

/** The error a timed-out statement rejects with. */
export class SaveTimeout extends Error {
  constructor() {
    super('the save server did not answer in time');
    this.name = 'TimeoutError';
  }
}

export function describeSlotFailure(raw: unknown, action: string): SlotFailure {
  const e = (raw ?? {}) as PgErrorLike;
  const code = typeof e.code === 'string' ? e.code : '';
  const message = typeof e.message === 'string' ? e.message : '';
  const status = typeof e.status === 'number' ? e.status : undefined;
  const detail = [message, typeof e.details === 'string' ? e.details : ''].filter(Boolean).join(' — ') || undefined;
  const lower = message.toLowerCase();

  if (
    e.name === 'TimeoutError' ||
    e.name === 'AbortError' ||
    e.name === 'TypeError' ||
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('network request failed') ||
    lower.includes('load failed') ||
    lower.includes('aborted')
  ) {
    return failure('offline', UNREACHABLE_MESSAGE, detail);
  }
  if (code === '42501' || status === 401 || status === 403) {
    return failure('denied', `Your sign-in has probably expired, so ${action} didn't happen. Sign out and back in, then try again.`, detail);
  }
  if (code === '42P01' || code === '42703') {
    return failure('table-missing', `Saving isn't set up on the server yet, so ${action} didn't happen. Nothing was changed.`, detail);
  }
  if (code === '23503') {
    return failure('not-signed-in', `This account no longer exists, so ${action} didn't happen. Register again to save.`, detail);
  }
  if (code === '23514') {
    return failure('corrupt', `The server turned the save down as malformed. Nothing was changed — this is worth reporting.`, detail);
  }
  if (status !== undefined && status >= 500) {
    return failure('server-error', `The save server had a problem (${status}), so ${action} didn't happen. Nothing was changed — try again shortly.`, detail);
  }
  if (status === 413 || lower.includes('too large')) {
    return failure('too-large', `The server turned the save down as too large. Nothing was changed.`, detail);
  }
  return failure(
    'unknown',
    message ? `${capitalise(action)} didn't work: ${message}. Nothing was changed.` : `${capitalise(action)} didn't work, and the server didn't say why. Nothing was changed.`,
    detail,
  );
}

const capitalise = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1) : s);

// ---------------------------------------------------------------------------
// The network calls
// ---------------------------------------------------------------------------

export type SlotClient = Pick<SupabaseClient, 'from' | 'auth'>;

const NOT_SIGNED_IN: SlotFailure = failure(
  'not-signed-in',
  'Sign in to save your league to your account. The game works signed out, and your league is still backed up on this device.',
);

/**
 * ONE STATEMENT, WITH A DEADLINE. D-EZ-99 (d): a save used to be able to say
 * "Saving…" forever. A statement that does not settle in time is abandoned —
 * through the builder's own `abortSignal` where it has one — and the player is
 * told in one plain sentence that their league is still on this device.
 */
export async function withDeadline<T>(statement: PromiseLike<T> & { abortSignal?: (s: AbortSignal) => unknown }, ms: number): Promise<T> {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let running: PromiseLike<T> = statement;
  if (controller && typeof statement.abortSignal === 'function') {
    running = statement.abortSignal(controller.signal) as PromiseLike<T>;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller?.abort();
      reject(new SaveTimeout());
    }, ms);
  });
  try {
    return await Promise.race([Promise.resolve(running), deadline]);
  } finally {
    clearTimeout(timer);
  }
}

async function currentUserId(client: SlotClient, ms: number = STATEMENT_TIMEOUT_MS): Promise<string | null> {
  const { data } = await withDeadline(client.auth.getSession(), ms);
  return data?.session?.user?.id ?? null;
}

const LABEL_COLUMNS =
  'kind, league_id, league_name, season_number, seed, race_number, season_key, saved_at, format_version';

interface DbResult {
  data: unknown;
  error: unknown;
}

/**
 * SAVE THE LEAGUE — the one Save button. D-EZ-99.
 *
 *   1. ask which seasons and races the server already holds (labels only);
 *   2. send the seasons it does not (once each, ever);
 *   3. send the races it does not (normally one);
 *   4. write the head — the commit;
 *   5. tidy rows from any other league or season.
 *
 * Does not confirm anything: the interface does (`overwriteNeedsConfirm`).
 */
export async function saveLeague(
  league: LeagueToSave,
  client: SlotClient = defaultClient,
  options: { timeoutMs?: number } = {},
): Promise<SlotSaved | SlotFailure> {
  const small = options.timeoutMs ?? STATEMENT_TIMEOUT_MS;
  const big = options.timeoutMs ?? BIG_STATEMENT_TIMEOUT_MS;
  const pack = packLeague(league);
  if ('ok' in pack) return pack;

  let userId: string | null;
  try {
    userId = await currentUserId(client, small);
  } catch (e) {
    return describeSlotFailure(e, 'the save');
  }
  if (!userId) return NOT_SIGNED_IN;
  if (!compressionAvailable()) return NO_COMPRESSION;

  const { head } = pack;
  let bytes = 0;
  let seasonsWritten = 0;
  let racesWritten: number | null = null;

  try {
    // ---- 1 + 2. the seasons ------------------------------------------------
    const { data, error } = await withDeadline<DbResult>(
      client
        .from(SLOT_SEASON_TABLE)
        .select('season_number, season_seed')
        .eq('user_id', userId)
        .eq('league_id', head.leagueId) as unknown as PromiseLike<DbResult>,
      small,
    );
    if (error) return describeSlotFailure(error, 'the save');
    const held = new Map<number, number>();
    for (const r of (data as { season_number: number; season_seed: number }[] | null) ?? []) {
      held.set(Number(r.season_number), Number(r.season_seed));
    }
    for (const season of pack.seasons) {
      if (held.get(season.ref.seasonNumber) === season.ref.seed) continue;
      const row = await buildSeasonRow(userId, season.pack());
      const size = (row.payload as string).length;
      if (size > MAX_PAYLOAD_BYTES) {
        return failure('too-large', `Season ${season.ref.seasonNumber} is too large to save (${mb(size)} MB). Nothing was changed.`);
      }
      const sent = await withDeadline<DbResult>(
        client
          .from(SLOT_SEASON_TABLE)
          .upsert(row, { onConflict: 'user_id,league_id,season_number' }) as unknown as PromiseLike<DbResult>,
        big,
      );
      if (sent.error) return describeSlotFailure(sent.error, 'the save');
      bytes += size;
      seasonsWritten += 1;
    }

    // ---- 3. the races ------------------------------------------------------
    if (head.kind === 'season') {
      const got = await withDeadline<DbResult>(
        client
          .from(SLOT_RACE_TABLE)
          .select('race_number')
          .eq('user_id', userId)
          .eq('season_key', head.seasonKey) as unknown as PromiseLike<DbResult>,
        small,
      );
      if (got.error) return describeSlotFailure(got.error, 'the save');
      const have = new Set(((got.data as { race_number: number }[] | null) ?? []).map((r) => Number(r.race_number)));
      const missing = pack.races.filter((c) => !have.has(c.raceNumber));
      let batch: Record<string, unknown>[] = [];
      let batchBytes = 0;
      const flush = async (): Promise<SlotFailure | null> => {
        if (batch.length === 0) return null;
        const sent = await withDeadline<DbResult>(
          client
            .from(SLOT_RACE_TABLE)
            .upsert(batch, { onConflict: 'user_id,season_key,race_number', ignoreDuplicates: true }) as unknown as PromiseLike<DbResult>,
          big,
        );
        batch = [];
        batchBytes = 0;
        return sent.error ? describeSlotFailure(sent.error, 'the save') : null;
      };
      for (const chunk of missing) {
        const row = await buildSeasonRaceRow(userId, head.seasonKey, chunk);
        const size = (row.payload as string).length;
        if (size > MAX_PAYLOAD_BYTES) {
          return failure('too-large', `Race ${chunk.raceNumber} is too large to save (${mb(size)} MB). Nothing was changed.`);
        }
        if (batchBytes + size > MAX_CHUNK_UPLOAD_BYTES) {
          const failed = await flush();
          if (failed) return failed;
        }
        batch.push(row);
        batchBytes += size;
        bytes += size;
      }
      const failed = await flush();
      if (failed) return failed;
      racesWritten = missing.length;
    }

    // ---- 4. the head: the commit -------------------------------------------
    const headRow = await buildHeadRow(userId, head);
    const headBytes = (headRow.payload as string).length;
    if (headBytes > MAX_PAYLOAD_BYTES) {
      return failure('too-large', `The league is too large to save (${mb(headBytes)} MB). Nothing was changed.`);
    }
    bytes += headBytes;
    const wrote = await withDeadline<DbResult>(
      client.from(SLOT_TABLE).upsert(headRow, { onConflict: 'user_id' }).select(LABEL_COLUMNS).single() as unknown as PromiseLike<DbResult>,
      small,
    );
    if (wrote.error) return describeSlotFailure(wrote.error, 'the save');
    const summary = describeSlotRow(wrote.data as Record<string, unknown>);
    if (!summary) {
      return failure('unknown', 'The save went through but the server did not confirm it. Load your league to check before saving again.');
    }

    // ---- 5. tidy up (failures here cost unreferenced rows, never the save) --
    await tidy(client, userId, head);

    return {
      ok: true,
      summary,
      bytes,
      seasonsWritten,
      seasonsHeld: pack.seasons.length,
      racesWritten,
      racesHeld: head.kind === 'season' ? pack.races.length : null,
    };
  } catch (e) {
    return describeSlotFailure(e, 'the save');
  }
}

/** Remove rows no longer part of the save: other leagues' seasons, other seasons' races. */
async function tidy(client: SlotClient, userId: string, head: LeagueHead | null): Promise<void> {
  try {
    const seasons = client.from(SLOT_SEASON_TABLE).delete().eq('user_id', userId);
    await withDeadline<DbResult>(
      (head ? seasons.neq('league_id', head.leagueId) : seasons) as unknown as PromiseLike<DbResult>,
      STATEMENT_TIMEOUT_MS,
    );
    const races = client.from(SLOT_RACE_TABLE).delete().eq('user_id', userId);
    await withDeadline<DbResult>(
      (head?.kind === 'season' ? races.neq('season_key', head.seasonKey) : races) as unknown as PromiseLike<DbResult>,
      STATEMENT_TIMEOUT_MS,
    );
  } catch {
    /* a tidy-up; the next save tries again */
  }
}

/** PEEK — is there a save, and what is it? Labels only, never the payload. */
export async function peekSlot(client: SlotClient = defaultClient): Promise<SlotPeeked | SlotFailure> {
  let userId: string | null;
  try {
    userId = await currentUserId(client);
  } catch (e) {
    return describeSlotFailure(e, 'checking your saved league');
  }
  if (!userId) return NOT_SIGNED_IN;
  try {
    const { data, error } = await withDeadline<DbResult>(
      client.from(SLOT_TABLE).select(LABEL_COLUMNS).eq('user_id', userId).maybeSingle() as unknown as PromiseLike<DbResult>,
      STATEMENT_TIMEOUT_MS,
    );
    if (error) return describeSlotFailure(error, 'checking your saved league');
    return { ok: true, summary: describeSlotRow(data as Record<string, unknown> | null) };
  } catch (e) {
    return describeSlotFailure(e, 'checking your saved league');
  }
}

/** LOAD THE LEAGUE — the one Load button. */
export async function loadLeague(
  client: SlotClient = defaultClient,
  options: { timeoutMs?: number } = {},
): Promise<SlotLoaded | SlotFailure> {
  const small = options.timeoutMs ?? STATEMENT_TIMEOUT_MS;
  const big = options.timeoutMs ?? BIG_STATEMENT_TIMEOUT_MS;
  let userId: string | null;
  try {
    userId = await currentUserId(client, small);
  } catch (e) {
    return describeSlotFailure(e, 'loading your league');
  }
  if (!userId) return NOT_SIGNED_IN;

  try {
    const { data, error } = await withDeadline<DbResult>(
      client
        .from(SLOT_TABLE)
        .select(`${LABEL_COLUMNS}, payload_encoding, payload`)
        .eq('user_id', userId)
        .maybeSingle() as unknown as PromiseLike<DbResult>,
      small,
    );
    if (error) return describeSlotFailure(error, 'loading your league');
    if (!data) return failure('no-saved-league', "You haven't saved a league yet. Play a season, press Save league, and it will be here next time.");

    const row = data as Record<string, unknown>;
    const savedAt = String(row.saved_at ?? '');
    // The label column answers "is this an old save?" without unpacking it.
    const early = versionProblem(Number(row.format_version ?? 0));
    if (early) return early;
    if (String(row.payload_encoding ?? '') !== SLOT_ENCODING) return failure('format-too-new', 'This save was made by a newer version of the game. Reload the page to pick it up.');

    const raw = await decodeBlobJson(row.payload);
    if ('ok' in raw) return raw;
    const head = readLeagueHead(raw.value);
    if ('ok' in head) return head;

    // ---- the seasons, one request each -------------------------------------
    const seasons: PackedSeason[] = [];
    for (const ref of head.seasons) {
      const got = await withDeadline<DbResult>(
        client
          .from(SLOT_SEASON_TABLE)
          .select('season_number, season_seed, payload_encoding, payload')
          .eq('user_id', userId)
          .eq('league_id', head.leagueId)
          .eq('season_number', ref.seasonNumber)
          .maybeSingle() as unknown as PromiseLike<DbResult>,
        big,
      );
      if (got.error) return describeSlotFailure(got.error, 'loading your league');
      const stored = got.data as Record<string, unknown> | null;
      if (!stored) return failure('corrupt', `Season ${ref.seasonNumber} of the saved league is missing, so it can't be opened. Press Load again in case a save was still finishing.`);
      const decoded = await decodeBlobJson(stored.payload);
      if ('ok' in decoded) return decoded;
      const packed = readPackedSeason(decoded.value, ref, head.leagueId);
      if ('ok' in packed) return packed;
      seasons.push(packed);
    }

    // ---- the races of the season in progress, in pages ---------------------
    const races: SeasonRaceChunk[] = [];
    if (head.kind === 'season') {
      for (let from = 1; from <= head.racesRun; from += CHUNK_PAGE_RACES) {
        const to = Math.min(head.racesRun, from + CHUNK_PAGE_RACES - 1);
        const got = await withDeadline<DbResult>(
          client
            .from(SLOT_RACE_TABLE)
            .select('race_number, payload_encoding, payload')
            .eq('user_id', userId)
            .eq('season_key', head.seasonKey)
            .gte('race_number', from)
            .lte('race_number', to)
            .order('race_number') as unknown as PromiseLike<DbResult>,
          big,
        );
        if (got.error) return describeSlotFailure(got.error, 'loading your league');
        const byNumber = new Map<number, Record<string, unknown>>();
        for (const r of (got.data as Record<string, unknown>[] | null) ?? []) byNumber.set(Number(r.race_number), r);
        for (let n = from; n <= to; n++) {
          const stored = byNumber.get(n);
          if (!stored) return failure('corrupt', `Race ${n} of the saved season is missing, so it can't be opened. Press Load again in case a save was still finishing.`);
          const decoded = await decodeBlobJson(stored.payload);
          if ('ok' in decoded) return decoded;
          const chunk = readSeasonRaceRow(decoded.value, n);
          if ('ok' in chunk) return chunk;
          races.push(chunk);
        }
      }
    }

    const opened = unpackLeague(head, seasons, races, savedAt);
    if ('ok' in opened) return opened;
    return { ok: true, league: opened };
  } catch (e) {
    return describeSlotFailure(e, 'loading your league');
  }
}

/** DELETE — empties the account's save: the head first, then the rows it named. */
export async function deleteSlot(client: SlotClient = defaultClient): Promise<SlotDeleted | SlotFailure> {
  let userId: string | null;
  try {
    userId = await currentUserId(client);
  } catch (e) {
    return describeSlotFailure(e, 'deleting your saved league');
  }
  if (!userId) return NOT_SIGNED_IN;
  try {
    const { data, error } = await withDeadline<DbResult>(
      client.from(SLOT_TABLE).delete().eq('user_id', userId).select(LABEL_COLUMNS).maybeSingle() as unknown as PromiseLike<DbResult>,
      STATEMENT_TIMEOUT_MS,
    );
    if (error) return describeSlotFailure(error, 'deleting your saved league');
    await tidy(client, userId, null);
    return { ok: true, removed: describeSlotRow(data as Record<string, unknown> | null) };
  } catch (e) {
    return describeSlotFailure(e, 'deleting your saved league');
  }
}

/** One decimal place, for a size in a sentence. */
export const mb = (bytes: number): string => (bytes / (1024 * 1024)).toFixed(1);
